import React, { useCallback, useEffect, useRef, useState } from "react";
import Modal from "../../CommonComponents/TransactionModal";
import ConfirmationModal from "../../CommonComponents/ConfirmationModal";
import { toast } from "react-toastify";
import { createExpense, fetchExpenses } from "../../../services/expenseApi";
import { fetchBranches } from "../../../services/branchApi";
import { authStorage } from "../../../utils/authStorage";
import { fetchUsers } from "../../../services/usersApi";
import Select from "react-select";
import { CalendarDays, FileText, Filter, ReceiptText, WalletCards } from "lucide-react";

const formatLocalDate = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export default function ExpensesPage() {
  const getMonthRange = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { startDate: formatLocalDate(start), endDate: formatLocalDate(end) };
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState(() => getMonthRange());
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [adminFilters, setAdminFilters] = useState({ branch_id: "", user_id: "" });
  const [form, setForm] = useState({
    date: "",
    category: "",
    categoryOther: "",
    description: "",
    amount: "",
    paymentMethod: "cash",
    receiptNo: "",
    notes: "",
    branch_id: "",
  });
  const [errors, setErrors] = useState({});
  const [detailsExpense, setDetailsExpense] = useState(null);
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [isSavingExpense, setIsSavingExpense] = useState(false);
  const [filterError, setFilterError] = useState("");
  const [branches, setBranches] = useState([]);
  const [cashiers, setCashiers] = useState([]);
  const filtersRef = useRef(filters);
  const adminFiltersRef = useRef(adminFilters);
  const isAdmin = authStorage.getUser()?.user_type === 1;
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 48,
      borderColor: state.isFocused ? "var(--color-primary)" : "#e2e8f0",
      boxShadow: state.isFocused ? "0 0 0 4px color-mix(in srgb, var(--color-primary) 14%, white)" : "none",
      borderRadius: 16,
      "&:hover": { borderColor: "var(--color-primary)" },
      fontSize: 14,
      backgroundColor: "#fff",
      paddingLeft: 4,
    }),
    valueContainer: (base) => ({ ...base, padding: "2px 12px" }),
    indicatorSeparator: (base) => ({ ...base, backgroundColor: "#e2e8f0" }),
    menu: (base) => ({
      ...base,
      borderRadius: 16,
      border: "1px solid #e2e8f0",
      overflow: "hidden",
      zIndex: 80,
      boxShadow: "0 18px 45px rgba(15,23,42,0.14)",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 120 }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "var(--color-primary)"
        : state.isFocused
          ? "rgba(0,0,0,0.04)"
          : "white",
      color: state.isSelected ? "#fff" : "#111827",
      fontSize: 14,
      padding: "10px 14px",
    }),
    singleValue: (base) => ({
      ...base,
      color: "#111827",
    }),
    placeholder: (base) => ({
      ...base,
      color: "#9ca3af",
    }),
  };
  const branchOptions = [
    { value: "", label: "All Branches" },
    ...branches.map((b) => ({ value: String(b.id), label: b.name })),
  ];
  const modalBranchOptions = [
    { value: "", label: "Select branch" },
    ...branches.map((b) => ({ value: String(b.id), label: b.name })),
  ];
  const categoryOptions = [
    { value: "", label: "Select category" },
    { value: "Supplies", label: "Supplies" },
    { value: "Transport", label: "Transport" },
    { value: "Others", label: "Others" },
  ];
  const paymentMethodOptions = [
    { value: "cash", label: "Cash" },
    { value: "gcash", label: "GCash" },
  ];
  const filteredCashiers = adminFilters.branch_id
    ? cashiers.filter(
      (u) => String(u.branch_id ?? u.branch?.id ?? "") === String(adminFilters.branch_id)
    )
    : cashiers;
  const cashierOptions = [
    { value: "", label: "All Cashiers / Admin" },
    ...filteredCashiers.map((u) => ({ value: String(u.id), label: u.username || u.email })),
  ];

  useEffect(() => {
    if (!isModalOpen) return;
    const today = formatLocalDate(new Date());
    setForm((prev) => ({ ...prev, date: today }));
  }, [isModalOpen]);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    adminFiltersRef.current = adminFilters;
  }, [adminFilters]);

  const loadExpenses = useCallback((nextFilters, nextAdminFilters) => {
    const activeFilters = nextFilters ?? filtersRef.current;
    const activeAdminFilters = nextAdminFilters ?? adminFiltersRef.current;
    setIsLoading(true);
    const params = {};
    if (activeFilters.startDate) params.startDate = activeFilters.startDate;
    if (activeFilters.endDate) params.endDate = activeFilters.endDate;
    if (isAdmin && activeAdminFilters.branch_id) params.branch_id = activeAdminFilters.branch_id;
    if (isAdmin && activeAdminFilters.user_id) params.user_id = activeAdminFilters.user_id;
    if ((params.startDate && !params.endDate) || (!params.startDate && params.endDate)) {
      setFilterError("Please select both From and To dates.");
      setIsLoading(false);
      return;
    }
    if (params.startDate && params.endDate && params.startDate > params.endDate) {
      setFilterError("From date cannot be later than To date.");
      setIsLoading(false);
      return;
    }
    setFilterError("");
    if (!params.startDate && !params.endDate) {
      setExpenses([]);
      setIsLoading(false);
      return;
    }
    fetchExpenses(params)
      .then((res) => {
        setExpenses(res.data || []);
        setCurrentPage(1);
      })
      .catch((error) => {
        const msg = error.response?.data?.message || "Failed to fetch expenses";
        toast.error(msg);
      })
      .finally(() => setIsLoading(false));
  }, [isAdmin]);

  useEffect(() => {
    loadExpenses();
  }, [loadExpenses]);

  const handleAdminFilterChange = (key, value) => {
    const nextAdminFilters = { ...adminFilters, [key]: value };
    if (key === "branch_id" && value && nextAdminFilters.user_id) {
      const selectedUser = cashiers.find((u) => String(u.id) === String(nextAdminFilters.user_id));
      const isSelectedUserInBranch =
        selectedUser &&
        String(selectedUser.branch_id ?? selectedUser.branch?.id ?? "") === String(value);
      if (!isSelectedUserInBranch) {
        nextAdminFilters.user_id = "";
      }
    }
    setAdminFilters(nextAdminFilters);
    loadExpenses(filters, nextAdminFilters);
  };

  useEffect(() => {
    if (!isAdmin) return;
    fetchBranches()
      .then((res) => {
        const data = res.data?.data ?? res.data ?? [];
        setBranches(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        toast.error("Failed to load branches");
      });
    fetchUsers()
      .then((res) => {
        const data = res.data?.data ?? res.data ?? [];
        setCashiers(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        toast.error("Failed to load users");
      });
  }, [isAdmin]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSelectChange = (name, option) => {
    const value = option?.value || "";
    setForm((prev) => {
      if (name === "category" && value !== "Others") {
        return { ...prev, [name]: value, categoryOther: "" };
      }
      return { ...prev, [name]: value };
    });
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    if (name === "category" && errors.categoryOther) {
      setErrors((prev) => ({ ...prev, categoryOther: "" }));
    }
  };

  const validateExpenseForm = () => {
    const nextErrors = {};
    if (!form.date) nextErrors.date = "Date is required.";
    if (!form.category) nextErrors.category = "Category is required.";
    if (form.category === "Others" && !form.categoryOther.trim()) {
      nextErrors.categoryOther = "Please specify the category.";
    }
    if (!form.description.trim()) nextErrors.description = "Description is required.";
    if (form.amount === "" || Number(form.amount) <= 0) {
      nextErrors.amount = "Amount must be greater than 0.";
    }
    if (isAdmin && !form.branch_id) {
      nextErrors.branch_id = "Branch is required.";
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return false;
    }

    setErrors({});
    return true;
  };

  const getExpensePayload = () => ({
    date: form.date,
    category: form.category,
    categoryOther: form.categoryOther,
    description: form.description,
    amount: form.amount,
    paymentMethod: form.paymentMethod,
    receiptNo: form.receiptNo,
    notes: form.notes,
    branch_id: isAdmin ? form.branch_id : undefined,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateExpenseForm()) return;
    setConfirmSaveOpen(true);
  };

  const handleConfirmSave = () => {
    if (isSavingExpense) return;
    setIsSavingExpense(true);
    createExpense({
      ...getExpensePayload(),
    })
      .then(() => {
        toast.success("Expense saved");
        setConfirmSaveOpen(false);
        setIsModalOpen(false);
        setForm((prev) => ({
          ...prev,
          category: "",
          categoryOther: "",
          description: "",
          amount: "",
          paymentMethod: "cash",
          receiptNo: "",
          notes: "",
          branch_id: "",
        }));
        loadExpenses();
      })
      .catch((error) => {
        const msg = error.response?.data?.message || "Failed to save expense";
        toast.error(msg);
      })
      .finally(() => {
        setIsSavingExpense(false);
      });
  };

  const totalPages = Math.max(1, Math.ceil(expenses.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const pagedExpenses = expenses.slice(startIndex, startIndex + itemsPerPage);
  const tableColCount = isAdmin ? 8 : 7;
  const totalSelectedAmount = expenses.reduce(
    (sum, exp) => sum + Number(exp.amount || 0),
    0
  );
  const dateInputClass = "h-12 rounded-2xl border border-slate-200 bg-white px-4 text-sm text-slate-900 shadow-sm outline-none transition focus:border-[var(--color-primary)] focus:ring-4 focus:ring-slate-100";
  const modalInputClass = "mt-1 h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-[var(--color-primary)] focus:ring-4 focus:ring-slate-100";
  const labelClass = "text-xs font-semibold uppercase tracking-[0.16em] text-slate-400";

  return (
    <div className="flex w-full max-w-full min-w-0 flex-col gap-4 pt-3">
      <section className="relative min-h-[176px] w-full max-w-full min-w-0 overflow-visible rounded-[30px] border border-slate-200/85 bg-[linear-gradient(145deg,#fffafa,#fffdfd,#ffffff)] p-5 shadow-[0_24px_80px_rgba(15,23,42,0.08)] sm:p-6">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full blur-3xl" style={{ background: "color-mix(in srgb, var(--color-primary) 18%, transparent)" }} />
          <div className="absolute left-10 top-0 h-28 w-28 rounded-full bg-white/60 blur-2xl" />
        </div>
        <div className="relative flex min-h-[120px] flex-col justify-between gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/70 bg-white/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 shadow-sm">
              <ReceiptText className="h-3.5 w-3.5" />
              Expense Workspace
            </div>
            <h1 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950">Expenses</h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
              Track daily operating costs, review payment details, and keep expense records organized.
            </p>
          </div>
          <div className="flex shrink-0 lg:self-start">
            <button
              type="button"
              className="inline-flex h-12 items-center justify-center gap-2 rounded-2xl px-5 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
              style={{ background: "var(--color-primary)" }}
              onClick={() => setIsModalOpen(true)}
            >
              Add Expense
            </button>
          </div>
        </div>
      </section>

      <section className="grid w-full max-w-full min-w-0 gap-3 md:grid-cols-3">
        <div className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-[0_12px_30px_rgba(15,23,42,0.06)]">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
            <WalletCards className="h-4 w-4" />
            Total
          </div>
          <p className="mt-3 text-2xl font-semibold text-slate-950">
            ₱ {totalSelectedAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
          <p className="mt-1 text-xs text-slate-500">Expenses in current filter</p>
        </div>
        <div className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-[0_12px_30px_rgba(15,23,42,0.06)]">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
            <FileText className="h-4 w-4" />
            Records
          </div>
          <p className="mt-3 text-2xl font-semibold text-slate-950">{expenses.length}</p>
          <p className="mt-1 text-xs text-slate-500">Matching expense entries</p>
        </div>
        <div className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-[0_12px_30px_rgba(15,23,42,0.06)]">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
            <CalendarDays className="h-4 w-4" />
            Range
          </div>
          <p className="mt-3 truncate text-2xl font-semibold text-slate-950">
            {filters.startDate && filters.endDate ? "Custom" : "None"}
          </p>
          <p className="mt-1 truncate text-xs text-slate-500">
            {filters.startDate && filters.endDate ? `${filters.startDate} to ${filters.endDate}` : "No date range selected"}
          </p>
        </div>
      </section>

      <section className="w-full max-w-full min-w-0 rounded-[30px] border border-slate-200/80 bg-white/95 p-5 shadow-[0_18px_60px_rgba(15,23,42,0.07)] sm:p-6">
        <div className="mb-5 flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-white shadow-sm" style={{ background: "var(--color-primary)" }}>
            <Filter className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-950">Expense Filters</h2>
            <p className="mt-1 text-sm text-slate-500">Default view shows expenses for the current month.</p>
          </div>
        </div>

        <div className="grid gap-4 rounded-[24px] border border-slate-200 bg-slate-50/70 p-4 lg:grid-cols-[1fr_1fr_auto] lg:items-end">
          <label className="flex flex-col gap-1 text-sm font-semibold text-slate-600">
            From
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => {
                setFilters((prev) => ({ ...prev, startDate: e.target.value }));
                if (filterError) setFilterError("");
              }}
              className={dateInputClass}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm font-semibold text-slate-600">
            To
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => {
                setFilters((prev) => ({ ...prev, endDate: e.target.value }));
                if (filterError) setFilterError("");
              }}
              className={dateInputClass}
            />
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              className="h-12 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-600 shadow-sm transition hover:bg-slate-100"
              onClick={() => {
                setFilters({ startDate: "", endDate: "" });
                setFilterError("");
                setExpenses([]);
                setCurrentPage(1);
                setAdminFilters({ branch_id: "", user_id: "" });
              }}
            >
              Clear
            </button>
            <button
              type="button"
              className="h-12 rounded-2xl px-5 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
              style={{ background: "var(--color-primary)" }}
              onClick={() => {
                if (isAdmin) {
                  const resetAdmin = { branch_id: "", user_id: "" };
                  setAdminFilters(resetAdmin);
                  loadExpenses(filters, resetAdmin);
                  return;
                }
                loadExpenses();
              }}
            >
              Apply
            </button>
          </div>
        </div>
        {filterError && <div className="mt-3 text-xs font-semibold text-red-500">{filterError}</div>}

        {isAdmin && (
          <div className="mt-4 grid gap-4 rounded-[24px] border border-slate-200 bg-slate-50/70 p-4 sm:grid-cols-2">
            <div>
              <div className="mb-1 text-sm font-semibold text-slate-600">Branch</div>
              <Select
                options={branchOptions}
                value={branchOptions.find((o) => o.value === adminFilters.branch_id) || branchOptions[0]}
                onChange={(opt) => handleAdminFilterChange("branch_id", opt?.value || "")}
                classNamePrefix="react-select"
                isSearchable={false}
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>
            <div>
              <div className="mb-1 text-sm font-semibold text-slate-600">Cashier / Admin</div>
              <Select
                options={cashierOptions}
                value={cashierOptions.find((o) => o.value === adminFilters.user_id) || cashierOptions[0]}
                onChange={(opt) => handleAdminFilterChange("user_id", opt?.value || "")}
                classNamePrefix="react-select"
                isSearchable={false}
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>
          </div>
        )}
      </section>

      <section className="w-full max-w-full min-w-0 rounded-[30px] border border-slate-200/80 bg-white/95 p-5 shadow-[0_18px_60px_rgba(15,23,42,0.07)] sm:p-6">
        <div className="mb-5 flex items-end justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-950">Expense Records</h2>
            <p className="mt-1 text-sm text-slate-500">Review saved expenses and open full details when needed.</p>
          </div>
          <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-500">
            {expenses.length} records
          </span>
        </div>

        <div className="max-w-full min-w-0 overflow-x-auto rounded-[28px] border border-slate-200/80 bg-white shadow-[0_18px_45px_rgba(15,23,42,0.06)]">
          <table className="min-w-[1040px] w-full border-collapse xl:min-w-0">
            <thead
              className="border-b"
              style={{
                background: "linear-gradient(180deg, var(--color-primary), color-mix(in srgb, var(--color-primary) 82%, black))",
                borderColor: "color-mix(in srgb, var(--color-primary) 72%, black)",
              }}
            >
              <tr>
                <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Date</th>
                <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Category</th>
                <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Description</th>
                <th className="px-4 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Amount</th>
                <th className="px-4 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Payment</th>
                <th className="px-4 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Branch</th>
                {isAdmin && <th className="px-4 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Cashier</th>}
                <th className="px-4 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Action</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={tableColCount} className="px-6 py-12 text-center text-sm text-slate-500">Loading expenses...</td>
                </tr>
              )}
              {!isLoading && expenses.length === 0 && (
                <tr>
                  <td colSpan={tableColCount} className="px-6 py-12 text-center text-sm text-slate-500">No expenses found.</td>
                </tr>
              )}
              {!isLoading && pagedExpenses.map((exp) => (
                <tr key={exp.id} className="border-b border-slate-100 bg-slate-50/85 transition hover:bg-emerald-50/70 even:bg-white last:border-0 sm:bg-transparent sm:hover:bg-slate-50/70 sm:even:bg-transparent">
                  <td className="px-4 py-3 text-sm font-medium text-slate-700 xl:px-5">{exp.date}</td>
                  <td className="px-4 py-3 xl:px-5">
                    <span className="inline-flex rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 shadow-sm">
                      {exp.category_other || exp.category}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600 xl:px-5">
                    <div className="max-w-[320px] truncate" title={exp.description || ""}>{exp.description}</div>
                  </td>
                  <td className="px-4 py-3 text-center xl:px-5">
                    <span className="inline-flex min-w-[112px] justify-center rounded-2xl border border-red-100 bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700">
                      ₱ {Number(exp.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center text-sm text-slate-600 xl:px-5">{exp.payment_method}</td>
                  <td className="px-4 py-3 text-center text-sm text-slate-600 xl:px-5">{exp.branch?.name || (exp.branch_id ? `Branch #${exp.branch_id}` : "-")}</td>
                  {isAdmin && (
                    <td className="px-4 py-3 text-center text-sm text-slate-600 xl:px-5">
                      {exp.user?.username || exp.user?.email || (exp.user_id ? `User #${exp.user_id}` : "-")}
                    </td>
                  )}
                  <td className="px-4 py-3 text-center xl:px-5">
                    <button
                      type="button"
                      className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50"
                      onClick={() => setDetailsExpense(exp)}
                    >
                      Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {expenses.length > 0 && (
          <div className="mt-4 flex items-center justify-between gap-3 overflow-x-auto text-xs whitespace-nowrap">
            <div className="shrink-0 text-gray-500">
              Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, expenses.length)} of {expenses.length}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button type="button" className="px-3 py-1.5 rounded-lg border text-xs disabled:opacity-50" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>Prev</button>
              <div className="text-xs text-gray-600">Page {currentPage} of {totalPages}</div>
              <button type="button" className="px-3 py-1.5 rounded-lg border text-xs disabled:opacity-50" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next</button>
            </div>
          </div>
        )}
      </section>

      <Modal open={!!detailsExpense} onClose={() => setDetailsExpense(null)}>
        {detailsExpense && (
          <div className="flex max-h-[88vh] w-full flex-col overflow-hidden">
            <div className="shrink-0 px-6 py-5 sm:px-8">
              <div className="flex items-start gap-4">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-white shadow-sm" style={{ background: "var(--color-primary)" }}>
                  <ReceiptText className="h-7 w-7" />
                </div>
                <div className="pr-10">
                  <h2 className="text-2xl font-semibold text-slate-950">Expense Details</h2>
                  <p className="mt-1 text-sm text-slate-500">Full expense information for review.</p>
                </div>
              </div>
            </div>
            <div className="grid min-h-0 grid-cols-1 gap-3 overflow-y-auto overflow-x-hidden px-6 text-sm sm:grid-cols-2 sm:px-8">
              <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                <div className={labelClass}>Date</div>
                <div className="mt-2 font-semibold text-slate-800">{detailsExpense.date || "-"}</div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                <div className={labelClass}>Category</div>
                <div className="mt-2 font-semibold text-slate-800">{detailsExpense.category_other || detailsExpense.category || "-"}</div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 sm:col-span-2">
                <div className={labelClass}>Description</div>
                <p className="mt-2 whitespace-pre-wrap break-words leading-relaxed text-slate-800">
                  {detailsExpense.description || "-"}
                </p>
              </div>
              <div className="rounded-2xl border border-red-100 bg-red-50 p-4">
                <div className={labelClass}>Amount</div>
                <div className="mt-2 font-semibold text-red-700">₱ {Number(detailsExpense.amount || 0).toFixed(2)}</div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                <div className={labelClass}>Payment Method</div>
                <div className="mt-2 font-semibold text-slate-800">{detailsExpense.payment_method || "-"}</div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                <div className={labelClass}>Branch</div>
                <div className="mt-2 font-semibold text-slate-800">
                  {detailsExpense.branch?.name || (detailsExpense.branch_id ? `Branch #${detailsExpense.branch_id}` : "-")}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                <div className={labelClass}>Cashier / Admin</div>
                <div className="mt-2 font-semibold text-slate-800">
                  {detailsExpense.user?.username || detailsExpense.user?.email || (detailsExpense.user_id ? `User #${detailsExpense.user_id}` : "-")}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                <div className={labelClass}>Receipt No.</div>
                <div className="mt-2 font-semibold text-slate-800">{detailsExpense.receipt_no || "-"}</div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 sm:col-span-2">
                <div className={labelClass}>Notes</div>
                <div className="mt-2 whitespace-pre-wrap text-slate-800">{detailsExpense.notes || "-"}</div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 sm:col-span-2">
                <div className={labelClass}>Created At</div>
                <div className="mt-2 font-semibold text-slate-800">
                  {detailsExpense.created_at
                    ? new Date(detailsExpense.created_at).toLocaleString()
                    : detailsExpense.createdAt
                      ? new Date(detailsExpense.createdAt).toLocaleString()
                      : "-"}
                </div>
              </div>
            </div>
            <div className="flex shrink-0 justify-end px-6 pb-6 pt-6 sm:px-8 sm:pb-8">
              <button
                type="button"
                className="h-11 rounded-2xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50"
                onClick={() => setDetailsExpense(null)}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <div className="flex max-h-[88vh] w-full flex-col overflow-hidden">
          <div className="shrink-0 px-6 py-5 sm:px-8">
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-white shadow-sm" style={{ background: "var(--color-primary)" }}>
                <ReceiptText className="h-7 w-7" />
              </div>
              <div className="pr-10">
                <h2 className="text-2xl font-semibold text-slate-950">Add Expense</h2>
                <p className="mt-1 text-sm leading-6 text-slate-500">Record a cashier expense entry with complete payment details.</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="min-h-0 w-full space-y-4 overflow-y-auto overflow-x-hidden px-6 pb-6 sm:px-8 sm:pb-8">
            <div className="rounded-[24px] border border-slate-200 bg-slate-50/70 p-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {isAdmin && (
              <div>
                <label className={labelClass}>
                  Branch <span className="text-red-500">*</span>
                </label>
                <div className="mt-1">
                  <Select
                    options={modalBranchOptions}
                    value={modalBranchOptions.find((o) => o.value === form.branch_id) || modalBranchOptions[0]}
                    onChange={(opt) => handleSelectChange("branch_id", opt)}
                    classNamePrefix="react-select"
                    isSearchable={false}
                    styles={selectStyles}
                  />
                </div>
                {errors.branch_id && (
                  <p className="mt-1 text-xs font-semibold text-red-500">{errors.branch_id}</p>
                )}
              </div>
            )}
            <div>
              <label className={labelClass}>
                Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="date"
                value={form.date}
                onChange={handleChange}
                className={modalInputClass}
              />
              {errors.date && (
                <p className="mt-1 text-xs font-semibold text-red-500">{errors.date}</p>
              )}
            </div>
            <div>
              <label className={labelClass}>
                Category <span className="text-red-500">*</span>
              </label>
              <div className="mt-1">
                <Select
                  options={categoryOptions}
                  value={categoryOptions.find((o) => o.value === form.category) || categoryOptions[0]}
                  onChange={(opt) => handleSelectChange("category", opt)}
                  classNamePrefix="react-select"
                  isSearchable={false}
                  styles={selectStyles}
                />
              </div>
              {errors.category && (
                <p className="mt-1 text-xs font-semibold text-red-500">{errors.category}</p>
              )}
            </div>
              </div>
            </div>

          {form.category === "Others" && (
            <div>
              <label className={labelClass}>
                Specify Category <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="categoryOther"
                value={form.categoryOther}
                onChange={handleChange}
                placeholder="Enter category"
                className={modalInputClass}
              />
              {errors.categoryOther && (
                <p className="mt-1 text-xs font-semibold text-red-500">{errors.categoryOther}</p>
              )}
            </div>
          )}

          <div className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm">
            <label className={labelClass}>
              Description <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder="What was this expense for?"
              className={modalInputClass}
            />
            {errors.description && (
              <p className="mt-1 text-xs font-semibold text-red-500">{errors.description}</p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass}>
                Amount <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="amount"
                value={form.amount}
                onChange={handleChange}
                placeholder="0.00"
                min="0"
                step="0.01"
                className={modalInputClass}
              />
              {errors.amount && (
                <p className="mt-1 text-xs font-semibold text-red-500">{errors.amount}</p>
              )}
            </div>
            <div>
              <label className={labelClass}>Payment Method</label>
              <div className="mt-1">
                <Select
                  options={paymentMethodOptions}
                  value={paymentMethodOptions.find((o) => o.value === form.paymentMethod) || paymentMethodOptions[0]}
                  onChange={(opt) => handleSelectChange("paymentMethod", opt)}
                  classNamePrefix="react-select"
                  isSearchable={false}
                  styles={selectStyles}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass}>Receipt No. (Optional)</label>
              <input
                type="text"
                name="receiptNo"
                value={form.receiptNo}
                onChange={handleChange}
                placeholder="OR # / Invoice #"
                className={modalInputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Notes (Optional)</label>
              <input
                type="text"
                name="notes"
                value={form.notes}
                onChange={handleChange}
                placeholder="Additional details"
                className={modalInputClass}
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="h-12 rounded-2xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="h-12 rounded-2xl px-5 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
              style={{ background: "var(--color-primary)" }}
            >
              Save Expense
            </button>
          </div>
          </form>
        </div>
      </Modal>

      <ConfirmationModal
        isOpen={confirmSaveOpen}
        onClose={() => {
          if (!isSavingExpense) setConfirmSaveOpen(false);
        }}
        onConfirm={handleConfirmSave}
        title="Save this expense?"
        message="Please confirm that the expense details are correct before saving."
        confirmText={isSavingExpense ? "Saving..." : "Save Expense"}
        cancelText="Review"
        confirmDisabled={isSavingExpense}
        useThemeColor
      >
        <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className={labelClass}>Date</div>
              <div className="mt-1 font-semibold text-slate-800">{form.date || "-"}</div>
            </div>
            <div>
              <div className={labelClass}>Amount</div>
              <div className="mt-1 font-semibold text-red-700">₱ {Number(form.amount || 0).toFixed(2)}</div>
            </div>
            <div className="col-span-2">
              <div className={labelClass}>Category</div>
              <div className="mt-1 font-semibold text-slate-800">
                {form.category === "Others" ? form.categoryOther : form.category || "-"}
              </div>
            </div>
            <div className="col-span-2">
              <div className={labelClass}>Description</div>
              <div className="mt-1 break-words text-slate-700">{form.description || "-"}</div>
            </div>
          </div>
        </div>
      </ConfirmationModal>
    </div>
  );
}
